import React from 'react';
import { Form, message, Tabs } from 'antd';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Button } from 'src/components';
import FormModalStyle from './FormModalStyle';
import { useAppDispatch, useAppSelector } from 'src/stores';
import { CheckboxField, InputField, SelectField, UploadImage } from 'src/components/form';
import { LIST_LANGUAGES } from 'src/constants/form';
import { upperCaseFirstWord } from 'src/utils/string';
import _ from 'lodash';
import {
  createPageAction,
  updatePageAction,
} from 'src/stores/screens/page/page.action';
import { PATH_REGEX, URL_REGEX } from 'src/constants';
import classNames from 'classnames';

interface FormModalProps {
  isModalFormOpen: boolean;
  handleCancel: () => void;
  handleOk: () => void;
  currentRecord?: Page.PageType | null;
  fetchData: (page?: number) => void;
  currentPage?: number;
}

const FormModal: React.FC<FormModalProps> = ({
  isModalFormOpen,
  handleCancel,
  handleOk,
  currentRecord,
  fetchData,
  currentPage,
}) => {
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.page);
  const { categories } = useAppSelector((state) => state.category);

  const validationSchema = yup.object().shape({
    categoryId: yup.string().required('Category is required'),
    slug: yup.string().nullable().max(100, 'Path must be at most 100 characters').required('Path is required').matches(PATH_REGEX, 'Invalid path'),
    url: yup.string().nullable().max(255, 'Project link must be at most 255 characters').matches(URL_REGEX, 'Invalid link'),
    fileUrl: yup.string().required('Image is required'),
    pageNameVi: yup.string().trim().max(100, 'Name must be at most 100 characters').required('Name is required'),
    pageNameEn: yup.string().trim().max(100, 'Name must be at most 100 characters').required('Name is required'),
    pageNameJa: yup.string().trim().max(100, 'Name must be at most 100 characters').required('Name is required'),
    pageNameKo: yup.string().trim().max(100, 'Name must be at most 100 characters').required('Name is required'),
  });

  const initialValues: Page.PagePostDetailParams = currentRecord?.id
    ? {
        ...currentRecord,
        categoryId: currentRecord.category.id
      } : {
        categoryId: '',
        slug: '',
        url: '',
        isShow: false,
        fileUrl: '',
        pageNameVi: '',
        pageNameEn: '',
        pageNameJa: '',
        pageNameKo: '',
      };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues,
    onSubmit: async (value: Page.PagePostDetailParams) => {
      const result: any = currentRecord?.id
        ? await dispatch(updatePageAction({ data: { ...value, id: currentRecord.id } }))
        : await dispatch(createPageAction({ data: value }));
      if (result?.payload?.error) {
        message.error(result?.payload?.message);
      } else {
        message.success(currentRecord?.id ? 'Update successful' : 'Creation successful');
        formik.resetForm({});
        fetchData(currentRecord?.id ? currentPage : 1)
        handleOk();
      }
    },
  });

  const onClose = () => {
    formik.resetForm({});
    handleCancel();
  };

  const renderListTabItems = LIST_LANGUAGES.map((item) => {
    const fieldNamePage = 'pageName' + upperCaseFirstWord(item);

    const isErrors = formik.errors[fieldNamePage]
    return (
      <Tabs.TabPane
        tabKey={item}
        tab={
          <span className={classNames({ error: isErrors })}>
            {item.toUpperCase()}
            <span style={{ color: 'red' }}> *</span>
          </span>
        }
        key={item}
      >
        <InputField
          className="form-control"
          label="Name"
          field={formik.getFieldProps(fieldNamePage)}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            size: 'middle',
            placeholder: 'Name',
          }}
          error={formik.errors[fieldNamePage]}
          touched={formik.touched[fieldNamePage]}
          required
        />
      </Tabs.TabPane>
    );
  });

  return (
    <FormModalStyle
      title={currentRecord?.id ? 'Update' : 'Create'}
      visible={isModalFormOpen}
      onCancel={onClose}
      className="modal-form"
      width={800}
    >
      <Form
        name="edit-form"
        className="setting-form"
        layout="vertical"
        onFinish={() => {
          formik.handleSubmit();
        }}
      >
        <SelectField
          label="Category"
          optionsSelect={
            _.isArray(categories?.records)
              ? categories.records.map((item) => ({
                value: item.id,
                label: item.nameEn,
              }))
              : []
          }
          field={formik.getFieldProps('categoryId')}
          setFieldValue={formik.setFieldValue}
          className="form-control"
          error={formik.errors.categoryId}
          touched={formik.touched.categoryId}
          placeholder="Category"
          required
        />
        <InputField
          className="form-control"
          label="Path"
          field={formik.getFieldProps('slug')}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            size: 'middle',
            placeholder: 'Path',
          }}
          error={formik.errors.slug}
          touched={formik.touched.slug}
          required
        />

        <InputField
          className="form-control"
          label="Project link"
          field={formik.getFieldProps('url')}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            size: 'middle',
            placeholder: 'Project link',
          }}
          error={formik.errors.url}
          touched={formik.touched.url}
          // required
        />

        <CheckboxField
          className="form-control checkbox"
          label="Show"
          field={formik.getFieldProps('isShow')}
          setFieldValue={formik.setFieldValue}
          error={formik.errors.isShow}
          touched={formik.touched.isShow}
        />

        <UploadImage
          className="form-control"
          label="Image (Recommend 16:9)"
          // label="Image/Video"
          field={formik.getFieldProps('fileUrl')}
          file={formik.getFieldProps('fileUrl').value}
          fileType={formik.values.fileType}
          setFieldValue={formik.setFieldValue}
          error={formik.errors.fileUrl}
          touched={formik.touched.fileUrl}
          required
        />

        <hr />

        <Tabs defaultActiveKey="en">{renderListTabItems}</Tabs>
        {/* Button footer */}
        <div className="button-footer">
          <Button buttonType="cancel" onClick={onClose} width={100}>
            Close
          </Button>
          <Button htmlType="submit" width={100} disabled={isLoading}>
            {currentRecord?.id ? 'Update' : 'Create'}
          </Button>
        </div>
      </Form>
    </FormModalStyle>
  );
};

export default FormModal;
